import { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { translations, type Language, type Translations, getTranslation } from '@/lib/i18n';

const LANGUAGE_STORAGE_KEY = 'dina-language';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  // Helper function for nested paths like t('common.save')
  translate: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'no';

  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored === 'no' || stored === 'en') {
    return stored;
  }

  // Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('en')) {
    return 'en';
  }

  // Default to Norwegian
  return 'no';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Initialize language synchronously to avoid blocking render
  const [language, setLanguageState] = useState<Language>(() => getInitialLanguage());

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    // Update HTML lang attribute
    document.documentElement.lang = lang;
  }, []);

  const t = translations[language];

  const translate = useCallback((path: string): string => {
    return getTranslation(t, path);
  }, [t]);

  // Update HTML lang attribute on language change
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    translate,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Shorthand hook for just translations
export function useTranslation() {
  const { t, translate, language } = useLanguage();
  return { t, translate, language };
}
